// ─────────────────────────────────────────────────────────────────────────
// The Spine — SHAPE cascade option bank (TELOS path B).
// Redone from the web's wrong Body/Self/Resources/Relationships categories
// into the five SHAPE sectors. See docs/OLMPS_SPINE_SPEC.md §1 and
// spine-types.ts for the shapes this file implements. Voice matches
// src/app/telos/cascade-presets.ts: plain, concrete, first-person.
// ─────────────────────────────────────────────────────────────────────────
import type { Sector } from '../domain/olmps';
import type { SectorCascade } from './spine-types';

// ── S · SOCIAL ──────────────────────────────────────────────────────────

const SOCIAL: SectorCascade = {
  sector: 'S',
  visions: [
    { id: 's-v1', text: 'Having bonds that make everyone involved stronger.' },
    { id: 's-v2', text: 'Having a voice that reaches exactly who it needs to.' },
    { id: 's-v3', text: 'Having a circle rooted in mutual truth and mutual value.' },
    { id: 's-v4', text: 'Having a reputation that precedes and outlasts me.' },
    { id: 's-v5', text: 'Having relationships that survive the truth.' },
    { id: 's-v6', text: 'Having a network I feed as much as it feeds me.' },
  ],
  deities: [
    {
      id: 'sofia',
      name: 'SOFIA',
      habit: 'The one who reaches out',
      domain: 'Communication & reach',
      tagline: 'The word that lands, said to the right person at the right time.',
      levels: [
        {
          level: 1,
          title: 'Sofia I — the Clear Speaker',
          skills: [
            'Says the needed thing instead of the comfortable thing',
            'Listens fully before responding',
            'Follows through on what she commits to out loud',
            'Reaches out before the silence gets long',
          ],
          activities: [
            'Sent one message to someone I have been meaning to reach',
            'Had one conversation with no phone in hand',
            'Said the thing I was avoiding saying, plainly',
            'Replied to every open message by end of day',
            'Asked one real question instead of a polite one',
          ],
        },
        {
          level: 2,
          title: 'Sofia II — the Trusted Voice',
          skills: [
            'Reads a room before deciding how to say something',
            'Builds reach on purpose, not by accident',
            'Turns follow-through into a reputation people can bank on',
            'Repairs friction before it calcifies',
            'Speaks for a group without losing her own voice',
          ],
          activities: [
            'Reached out to someone new who aligns with my direction',
            'Addressed a misunderstanding or unresolved tension directly',
            'Offered value to someone before asking for anything',
            'Shared something I made or know with the people it would help',
            'Followed up on a commitment before being asked',
          ],
        },
      ],
    },
    {
      id: 'kharis',
      name: 'KHARIS',
      habit: 'The generous friend',
      domain: 'Generosity & bonds',
      tagline: 'What is given freely returns compounded.',
      levels: [
        {
          level: 1,
          title: 'Kharis I — the Present One',
          skills: [
            'Gives full presence, not just time',
            'Does things for people with no expectation of return',
            'Remembers what matters to the people close to her',
            'Shows up for the small moments, not just the big ones',
          ],
          activities: [
            'Gave someone my full presence, phone away, for one conversation',
            'Did something for someone with no expectation of return',
            'Checked in on someone I have not asked about in a while',
            'Remembered and acted on something specific someone told me',
          ],
        },
        {
          level: 2,
          title: 'Kharis II — the Anchor',
          skills: [
            'Sets and holds boundaries without guilt',
            'Builds rituals that keep close bonds close',
            'Extends trust first, deliberately',
            'Holds space for people through their hard weeks, not just their good ones',
            'Turns acquaintances into real relationships on purpose',
          ],
          activities: [
            'Enforced a boundary I already communicated, without apologizing',
            'Ran a standing ritual with someone close (call, meal, walk)',
            'Sat with someone through a hard moment without trying to fix it',
            'Said no to something that did not serve the relationship',
            'Invested in one relationship that has been drifting',
          ],
        },
      ],
    },
  ],
  aims: [
    {
      id: 's-aim-reachout',
      text: 'I reach out before the silence gets long.',
      protocol: [
        { id: 's-aim-reachout-1', text: 'Message one person I have been meaning to talk to, twice a week' },
        { id: 's-aim-reachout-2', text: 'One phone-free conversation a week, full presence' },
        { id: 's-aim-reachout-3', text: 'Review who has gone quiet at the end of each week' },
      ],
    },
    {
      id: 's-aim-honesty',
      text: 'I communicate with honesty, even when it costs something.',
      protocol: [
        { id: 's-aim-honesty-1', text: 'Say the needed thing within 48 hours instead of sitting on it' },
        { id: 's-aim-honesty-2', text: 'Ask one real question a week instead of a polite one' },
        { id: 's-aim-honesty-3', text: 'Address any unresolved tension before the week closes' },
      ],
    },
    {
      id: 's-aim-network',
      text: 'I expand my network with purpose, not by accident.',
      protocol: [
        { id: 's-aim-network-1', text: 'Connect with one new person aligned with my direction, weekly' },
        { id: 's-aim-network-2', text: 'Attend or participate in one community or group monthly' },
        { id: 's-aim-network-3', text: 'Offer value before asking for anything, at least once a week' },
      ],
    },
    {
      id: 's-aim-boundaries',
      text: 'I set and maintain boundaries.',
      protocol: [
        { id: 's-aim-boundaries-1', text: 'Name one boundary out loud when it is crossed' },
        { id: 's-aim-boundaries-2', text: 'Say no to one draining commitment a week' },
        { id: 's-aim-boundaries-3', text: 'Review which relationships are one-sided at month end' },
      ],
    },
    {
      id: 's-aim-reputation',
      text: 'I manage my reputation intentionally.',
      protocol: [
        { id: 's-aim-reputation-1', text: 'Follow through on every commitment made this week' },
        { id: 's-aim-reputation-2', text: 'Show up the way I want to be known, deliberately' },
        { id: 's-aim-reputation-3', text: 'Close the loop on anything I said I would do' },
      ],
    },
    {
      id: 's-aim-give',
      text: 'I invest in the people close to me without keeping score.',
      protocol: [
        { id: 's-aim-give-1', text: 'Do one unasked-for favor for someone close, weekly' },
        { id: 's-aim-give-2', text: 'Run a standing ritual with one close person (call, meal, walk)' },
        { id: 's-aim-give-3', text: 'Notice and act on one specific thing someone mentioned needing' },
      ],
    },
  ],
};

// ── H · HEALTH ──────────────────────────────────────────────────────────

const HEALTH: SectorCascade = {
  sector: 'H',
  visions: [
    { id: 'h-v1', text: 'Having physical capacity that never limits my life.' },
    { id: 'h-v2', text: 'Having a body that meets every demand I place on it.' },
    { id: 'h-v3', text: 'Having energy that is earned, never borrowed.' },
    { id: 'h-v4', text: 'Having a machine I maintain with the respect it deserves.' },
    { id: 'h-v5', text: 'Having recovery I trust as much as I trust effort.' },
    { id: 'h-v6', text: 'Having a body that performs when called upon.' },
  ],
  deities: [
    {
      id: 'hypnos',
      name: 'HYPNOS',
      habit: 'The early riser',
      domain: 'Sleep & recovery',
      tagline: 'Rest is a strategy, not a reward.',
      levels: [
        {
          level: 1,
          title: 'Hypnos I — the Regular Sleeper',
          skills: [
            'Holds a consistent bedtime and wake time',
            'Cuts screens before sleep',
            'Notices when a night was bad and why',
            'Protects the last hour of the day from new obligations',
          ],
          activities: [
            'In bed by 23:00 (no screen after 22:30)',
            'Woke within 30 min of the same time as yesterday',
            'Room dark and cool before lights out',
            'No caffeine after 14:00',
            'Logged how last night\'s sleep actually felt',
          ],
        },
        {
          level: 2,
          title: 'Hypnos II — the Recovery Architect',
          skills: [
            'Reads fatigue signals before they become a crash',
            'Builds a wind-down ritual that actually works, not a wish list',
            'Adjusts training and workload to recovery state',
            'Protects sleep through travel and disruption',
            'Treats a bad night as data, not a failure',
          ],
          activities: [
            'Ran the full wind-down ritual (dim lights, no work, fixed order) by 22:30',
            'Rated recovery on waking and adjusted the day\'s intensity to match',
            'Took one full rest day this week with no training load',
            'Kept sleep schedule within 1 hour of normal while traveling',
            'Reviewed the week\'s sleep log and named one pattern to fix',
          ],
        },
      ],
    },
    {
      id: 'hygia',
      name: 'HYGIA',
      habit: 'The consistent eater',
      domain: 'Nutrition & body maintenance',
      tagline: 'You are not what you eat — you are what you consistently eat.',
      levels: [
        {
          level: 1,
          title: 'Hygia I — the Consistent Eater',
          skills: [
            'Prepares or cooks real food most days',
            'Drinks water first thing and steadily through the day',
            'Moves the body on purpose daily',
            'Keeps basic hygiene and grooming non-negotiable',
          ],
          activities: [
            'Prepped or cooked at least one meal',
            'Drank water first thing in the morning',
            '30 min intentional movement',
            'Full hygiene routine completed',
            'Ate mostly whole foods today',
          ],
        },
        {
          level: 2,
          title: 'Hygia II — the Maintained Machine',
          skills: [
            'Plans meals ahead instead of reacting hungry',
            'Trains with structure, not just motion',
            'Stretches and mobilizes proactively, not after pain',
            'Keeps preventive care current instead of deferred',
            'Tracks a body metric that actually matters to her goal',
          ],
          activities: [
            'Planned tomorrow\'s meals tonight',
            'Completed a structured training session (not just "moved")',
            'Stretched or mobilized for 10 min',
            'Addressed a health item I have been putting off (appointment, checkup)',
            'Logged the one body metric I am tracking this month',
          ],
        },
      ],
    },
    {
      id: 'akeso',
      name: 'AKESO',
      habit: 'The one who recovers properly',
      domain: 'Healing & injury recovery',
      tagline: 'What you neglect now, you pay for later with interest.',
      levels: [
        {
          level: 1,
          title: 'Akeso I — the Attentive Healer',
          skills: [
            'Notices pain or strain before ignoring it',
            'Follows through on prescribed rehab or recovery work',
            'Rests an injury instead of pushing through it',
            'Keeps medications and treatments on schedule',
          ],
          activities: [
            'Took vitamins/meds on schedule',
            'Did today\'s rehab or mobility work for a known issue',
            'Checked in with how my body feels today',
            'Modified a workout to protect a known weak point',
          ],
        },
        {
          level: 2,
          title: 'Akeso II — the Self-Restorer',
          skills: [
            'Builds a full recovery protocol around a chronic issue, not a quick fix',
            'Knows when to seek professional help versus self-manage',
            'Tracks healing progress with real markers',
            'Prevents re-injury by addressing root cause, not just symptom',
            'Balances pushing capacity with protecting a healing area',
          ],
          activities: [
            'Ran the full rehab protocol, not just the easy parts',
            'Booked or attended a professional check-in for an ongoing issue',
            'Logged pain/mobility score to track real progress',
            'Identified and addressed the root cause behind a recurring niggle',
          ],
        },
      ],
    },
  ],
  aims: [
    {
      id: 'h-aim-sleep',
      text: 'I protect my rest.',
      protocol: [
        { id: 'h-aim-sleep-1', text: 'In bed by target time, screens off 30 min before' },
        { id: 'h-aim-sleep-2', text: 'Get 7+ hours, checked against a sleep log' },
        { id: 'h-aim-sleep-3', text: 'Review the week\'s sleep pattern every Sunday' },
      ],
    },
    {
      id: 'h-aim-move',
      text: 'I move consistently.',
      protocol: [
        { id: 'h-aim-move-1', text: '30 min intentional movement, most days' },
        { id: 'h-aim-move-2', text: 'Take the harder physical option (stairs, walked, carried)' },
        { id: 'h-aim-move-3', text: 'Stretch or mobilize for 10 min daily' },
      ],
    },
    {
      id: 'h-aim-eat',
      text: 'I eat with purpose.',
      protocol: [
        { id: 'h-aim-eat-1', text: 'Prep or cook at least one meal most days' },
        { id: 'h-aim-eat-2', text: 'Drink water first thing every morning' },
        { id: 'h-aim-eat-3', text: 'Plan the next day\'s meals the night before' },
      ],
    },
    {
      id: 'h-aim-checkup',
      text: 'I stay on top of my health instead of deferring it.',
      protocol: [
        { id: 'h-aim-checkup-1', text: 'Take vitamins/meds on schedule daily' },
        { id: 'h-aim-checkup-2', text: 'Address one deferred health item per month (appointment, checkup)' },
        { id: 'h-aim-checkup-3', text: 'Check in with how my body actually feels, weekly' },
      ],
    },
    {
      id: 'h-aim-hygiene',
      text: 'I keep myself clean and maintained.',
      protocol: [
        { id: 'h-aim-hygiene-1', text: 'Complete full hygiene routine daily' },
        { id: 'h-aim-hygiene-2', text: 'Handle grooming and appearance without letting it slide' },
        { id: 'h-aim-hygiene-3', text: 'Keep the space I live in clean, weekly reset' },
      ],
    },
    {
      id: 'h-aim-recovery',
      text: 'I treat recovery as training, not an afterthought.',
      protocol: [
        { id: 'h-aim-recovery-1', text: 'Take one full rest day a week, no exceptions' },
        { id: 'h-aim-recovery-2', text: 'Rate recovery each morning and adjust intensity to match' },
        { id: 'h-aim-recovery-3', text: 'Run rehab/mobility work for any known issue, daily' },
      ],
    },
  ],
};

// ── A · ADMIN ───────────────────────────────────────────────────────────

const ADMIN: SectorCascade = {
  sector: 'A',
  visions: [
    { id: 'a-v1', text: 'Having systems that run without my constant attention.' },
    { id: 'a-v2', text: 'Having every obligation tracked, so nothing surprises me.' },
    { id: 'a-v3', text: 'Having a life that requires no cleanup, because it never piles up.' },
    { id: 'a-v4', text: 'Having order I trust enough to stop checking.' },
    { id: 'a-v5', text: 'Having paperwork and logistics handled before they become urgent.' },
    { id: 'a-v6', text: 'Having an environment that works for me, never against me.' },
  ],
  deities: [
    {
      id: 'eunomia',
      name: 'EUNOMIA',
      habit: 'The organised operator',
      domain: 'Good order & logistics',
      tagline: 'Order is not decoration — it is what lets everything else run.',
      levels: [
        {
          level: 1,
          title: 'Eunomia I — the Organized One',
          skills: [
            'Handles paperwork the day it arrives, not the week it is due',
            'Keeps one place where every obligation lives',
            'Clears a physical space before it becomes clutter',
            'Pays bills and renewals on time, every time',
          ],
          activities: [
            'Processed inbox/mail to zero (filed, paid, or actioned)',
            'Checked the master obligations list and updated it',
            'Cleared one surface or drawer before it became a pile',
            'Paid or scheduled one bill/renewal due this week',
            'Filed one document instead of leaving it loose',
          ],
        },
        {
          level: 2,
          title: 'Eunomia II — the Systems Builder',
          skills: [
            'Builds recurring systems instead of solving the same problem twice',
            'Automates or delegates anything repeatable',
            'Runs a weekly review of every open obligation',
            'Keeps records organized well enough to find anything in under a minute',
            'Anticipates renewals and deadlines before they become urgent',
          ],
          activities: [
            'Set up or maintained one recurring reminder/automation for a repeat task',
            'Ran a full weekly admin review (bills, docs, deadlines, subscriptions)',
            'Delegated or automated one task that did not need to be done by hand',
            'Audited subscriptions/renewals for anything unused or upcoming',
            'Organized a records system so anything is findable in under a minute',
          ],
        },
      ],
    },
    {
      id: 'themistas',
      name: 'THEMISTAS',
      habit: 'The one who keeps records',
      domain: 'Compliance & documentation',
      tagline: 'What is not written down does not exist when it counts.',
      levels: [
        {
          level: 1,
          title: 'Themistas I — the Compliant One',
          skills: [
            'Keeps IDs, licenses, and policies current, never expired',
            'Reads before signing',
            'Backs up important documents',
            'Tracks deadlines with real lead time, not last-minute scrambles',
          ],
          activities: [
            'Checked expiry dates on one ID/license/policy this week',
            'Backed up or scanned one important document',
            'Read the full terms before signing or agreeing to something',
            'Logged a deadline into a calendar with lead-time buffer',
          ],
        },
        {
          level: 2,
          title: 'Themistas II — the Record Keeper',
          skills: [
            'Maintains a complete, current archive of every important document',
            'Reviews contracts and policies proactively, not reactively',
            'Keeps a compliance calendar that catches renewals months out',
            'Handles disputes and paperwork issues without panic',
            'Trains others (family, team) on where the records live',
          ],
          activities: [
            'Reviewed one contract/policy for anything that changed or needs action',
            'Updated the compliance calendar with a renewal 60+ days out',
            'Resolved one outstanding administrative dispute or discrepancy',
            'Confirmed someone else knows where critical records are kept',
          ],
        },
      ],
    },
  ],
  aims: [
    {
      id: 'a-aim-inbox',
      text: 'I process what comes in instead of letting it pile up.',
      protocol: [
        { id: 'a-aim-inbox-1', text: 'Clear inbox/mail to zero daily' },
        { id: 'a-aim-inbox-2', text: 'File or action anything paper within 48 hours' },
        { id: 'a-aim-inbox-3', text: 'Review the master obligations list weekly' },
      ],
    },
    {
      id: 'a-aim-bills',
      text: 'I keep bills and obligations current.',
      protocol: [
        { id: 'a-aim-bills-1', text: 'Pay or schedule every bill the week it arrives' },
        { id: 'a-aim-bills-2', text: 'Check on one account, policy, or document that matters, weekly' },
        { id: 'a-aim-bills-3', text: 'Audit subscriptions and renewals monthly' },
      ],
    },
    {
      id: 'a-aim-environment',
      text: 'I maintain my environment instead of letting it decay.',
      protocol: [
        { id: 'a-aim-environment-1', text: 'Clear one surface, drawer, or space before it piles up, weekly' },
        { id: 'a-aim-environment-2', text: 'Address one maintenance or repair issue this week' },
        { id: 'a-aim-environment-3', text: 'Run a full home/space reset weekly' },
      ],
    },
    {
      id: 'a-aim-systems',
      text: 'I build systems instead of solving the same problem twice.',
      protocol: [
        { id: 'a-aim-systems-1', text: 'Set up one recurring reminder or automation this month' },
        { id: 'a-aim-systems-2', text: 'Delegate or automate one repeatable task this month' },
        { id: 'a-aim-systems-3', text: 'Run a full weekly admin review' },
      ],
    },
    {
      id: 'a-aim-records',
      text: 'I keep records I can trust and find.',
      protocol: [
        { id: 'a-aim-records-1', text: 'File or scan one important document weekly' },
        { id: 'a-aim-records-2', text: 'Check expiry on one ID/license/policy monthly' },
        { id: 'a-aim-records-3', text: 'Confirm every deadline has a calendar entry with buffer' },
      ],
    },
    {
      id: 'a-aim-deadlines',
      text: 'I stay ahead of deadlines instead of scrambling at them.',
      protocol: [
        { id: 'a-aim-deadlines-1', text: 'Log every new deadline into a calendar the day it appears' },
        { id: 'a-aim-deadlines-2', text: 'Review upcoming deadlines every Sunday' },
        { id: 'a-aim-deadlines-3', text: 'Build in lead-time buffer, never file at the last moment' },
      ],
    },
  ],
};

// ── P · PERSONAL ───────────────────────────────────────────────────────

const PERSONAL: SectorCascade = {
  sector: 'P',
  visions: [
    { id: 'p-v1', text: 'Having a mind that operates with clarity in every situation.' },
    { id: 'p-v2', text: 'Having a character that requires no explanation.' },
    { id: 'p-v3', text: 'Having complete ownership of who I am and who I am becoming.' },
    { id: 'p-v4', text: 'Having a self I built on purpose, not by accident.' },
    { id: 'p-v5', text: 'Having an inner life that drives everything else forward.' },
    { id: 'p-v6', text: 'Having skills that compound faster than they decay.' },
  ],
  deities: [
    {
      id: 'mnemosyne',
      name: 'MNEMOSYNE',
      habit: 'The deliberate learner',
      domain: 'Learning & memory',
      tagline: 'Growth requires honest discomfort, not positive thinking.',
      levels: [
        {
          level: 1,
          title: 'Mnemosyne I — the Steady Student',
          skills: [
            'Reads or studies on a fixed schedule, not when inspired',
            'Practices an actual skill, not just consumes content',
            'Applies something learned within the week it was learned',
            'Reviews what she learned instead of letting it evaporate',
          ],
          activities: [
            'Read or studied for at least 20 min',
            'Practiced a skill I am developing',
            'Applied something new I recently learned',
            'Reviewed notes or takeaways from something learned this week',
          ],
        },
        {
          level: 2,
          title: 'Mnemosyne II — the Compounding Mind',
          skills: [
            'Builds a deliberate curriculum instead of random consumption',
            'Teaches or explains what she has learned to someone else',
            'Tracks skill progression with real benchmarks',
            'Seeks out discomfort as the fastest route to growth',
            'Connects new learning to existing knowledge on purpose',
          ],
          activities: [
            'Followed a deliberate learning plan, not random content',
            'Taught or explained a concept to someone else',
            'Practiced the hardest part of a skill, not the comfortable part',
            'Logged progress against a real skill benchmark',
            'Connected something new learned to something already known',
          ],
        },
      ],
    },
    {
      id: 'psyche',
      name: 'PSYCHE',
      habit: 'The self-aware one',
      domain: 'Reflection & inner ownership',
      tagline: 'An unexamined pattern will repeat until it is named.',
      levels: [
        {
          level: 1,
          title: 'Psyche I — the Honest Observer',
          skills: [
            'Journals or reflects on the day regularly',
            'Names an emotion instead of acting on it blindly',
            'Reviews yesterday\'s decisions honestly',
            'Pauses before reacting to something that triggered her',
          ],
          activities: [
            'Wrote in my journal today',
            'Named an emotion instead of acting on it blindly',
            'Reviewed yesterday\'s decisions honestly',
            'Paused before reacting to something that triggered me',
          ],
        },
        {
          level: 2,
          title: 'Psyche II — the Self-Authored',
          skills: [
            'Identifies recurring patterns and actively works to change them',
            'Chooses a response instead of defaulting to a reaction, consistently',
            'Builds identity through repeated action, not belief alone',
            'Sits with discomfort long enough to learn from it',
            'Holds a clear, self-authored sense of who she is becoming',
          ],
          activities: [
            'Identified one pattern I want to change or reinforce',
            'Chose a deliberate response instead of a default reaction',
            'Made something today that reflects who I am becoming (wrote, built, designed)',
            'Sat in silence, meditation, or prayer with no distraction',
            'Named one avoidance and did the thing anyway',
          ],
        },
      ],
    },
  ],
  aims: [
    {
      id: 'p-aim-learn',
      text: 'I learn consistently.',
      protocol: [
        { id: 'p-aim-learn-1', text: 'Read or study for at least 20 min daily' },
        { id: 'p-aim-learn-2', text: 'Practice a skill I am developing, several times a week' },
        { id: 'p-aim-learn-3', text: 'Apply one new thing learned within the same week' },
      ],
    },
    {
      id: 'p-aim-reflect',
      text: 'I reflect and journal.',
      protocol: [
        { id: 'p-aim-reflect-1', text: 'Write in a journal daily' },
        { id: 'p-aim-reflect-2', text: 'Review yesterday\'s decisions honestly, weekly' },
        { id: 'p-aim-reflect-3', text: 'Name one pattern I want to change or reinforce, weekly' },
      ],
    },
    {
      id: 'p-aim-emotion',
      text: 'I manage my emotional state instead of being run by it.',
      protocol: [
        { id: 'p-aim-emotion-1', text: 'Pause before reacting to something that triggered me' },
        { id: 'p-aim-emotion-2', text: 'Name the emotion instead of acting on it blindly' },
        { id: 'p-aim-emotion-3', text: 'Choose a response instead of defaulting to a reaction' },
      ],
    },
    {
      id: 'p-aim-create',
      text: 'I create consistently.',
      protocol: [
        { id: 'p-aim-create-1', text: 'Make something (write, build, design, compose) most days' },
        { id: 'p-aim-create-2', text: 'Work on my passion project weekly' },
        { id: 'p-aim-create-3', text: 'Protect one block of creative flow time weekly' },
      ],
    },
    {
      id: 'p-aim-ground',
      text: 'I stay mentally and spiritually grounded.',
      protocol: [
        { id: 'p-aim-ground-1', text: 'Meditate, pray, or sit in silence daily' },
        { id: 'p-aim-ground-2', text: 'Spend time in nature or away from noise, weekly' },
        { id: 'p-aim-ground-3', text: 'Practice gratitude intentionally, daily' },
      ],
    },
    {
      id: 'p-aim-skill',
      text: 'I build one skill on purpose instead of drifting between many.',
      protocol: [
        { id: 'p-aim-skill-1', text: 'Follow a deliberate learning plan for one named skill' },
        { id: 'p-aim-skill-2', text: 'Practice the hardest part of the skill weekly, not just the fun part' },
        { id: 'p-aim-skill-3', text: 'Log progress against a real benchmark monthly' },
      ],
    },
  ],
};

// ── E · ECONOMIC ──────────────────────────────────────────────────────

const ECONOMIC: SectorCascade = {
  sector: 'E',
  visions: [
    { id: 'e-v1', text: 'Having systems that generate freedom and eliminate dependency.' },
    { id: 'e-v2', text: 'Having a foundation that works independently of my time.' },
    { id: 'e-v3', text: 'Having more options every year and fewer dependencies.' },
    { id: 'e-v4', text: 'Having infrastructure that compounds whether I am present or not.' },
    { id: 'e-v5', text: 'Having a craft valuable enough to always earn.' },
    { id: 'e-v6', text: 'Having wealth built on decisions, not luck.' },
  ],
  deities: [
    {
      id: 'plutus',
      name: 'PLUTUS',
      habit: 'The one who tracks every dollar',
      domain: 'Money & assets',
      tagline: 'What you do not track, you lose.',
      levels: [
        {
          level: 1,
          title: 'Plutus I — the Tracker',
          skills: [
            'Reviews spending regularly instead of avoiding it',
            'Stays within a budget she actually set',
            'Puts something into savings on a schedule',
            'Knows her net position at any given time',
          ],
          activities: [
            'Reviewed my spending today',
            'Stayed within my daily/weekly budget',
            'Put something into savings (any amount)',
            'Checked one account balance and reconciled it mentally',
          ],
        },
        {
          level: 2,
          title: 'Plutus II — the Builder',
          skills: [
            'Contributes to long-term goals systematically, not sporadically',
            'Diversifies and protects assets deliberately',
            'Reviews and rebalances a financial plan on a set cadence',
            'Learns continuously about managing and growing money',
            'Makes decisions today that compound for years, not weeks',
          ],
          activities: [
            'Contributed to a long-term goal (retirement, education, asset)',
            'Reviewed and rebalanced one investment or savings allocation',
            'Learned something about managing money better',
            'Made a decision today that future me will thank me for',
            'Checked on an account, policy, or document that protects what I have built',
          ],
        },
      ],
    },
    {
      id: 'kerdos',
      name: 'KERDOS',
      habit: 'The one who sharpens their craft',
      domain: 'Earning & craft',
      tagline: 'Every resource is either growing or shrinking — nothing holds.',
      levels: [
        {
          level: 1,
          title: 'Kerdos I — the Deliberate Worker',
          skills: [
            'Works on what actually advances her position, not just what is busy',
            'Invests time in a skill with professional return',
            'Delivers what she commits to, on time',
            'Tracks her own value and asks for what it is worth',
          ],
          activities: [
            'Worked on a project or deliverable that advances my position',
            'Invested time in a skill with professional return',
            'Delivered something I committed to, on time',
            'Tracked hours/output against what I am actually paid or worth',
          ],
        },
        {
          level: 2,
          title: 'Kerdos II — the Compounder',
          skills: [
            'Builds income streams that do not require her constant presence',
            'Actively pursues opportunities instead of waiting for them',
            'Negotiates her worth instead of accepting the default offer',
            'Reinvests earnings into higher-leverage skills or assets',
            'Turns a single skill into multiple income paths',
          ],
          activities: [
            'Explored a new opportunity or income stream',
            'Negotiated or asked for something I was underpaid for',
            'Reinvested earnings into a skill or asset with future leverage',
            'Worked on a system or asset that earns without my constant presence',
            'Followed up on a professional opportunity instead of letting it go cold',
          ],
        },
      ],
    },
  ],
  aims: [
    {
      id: 'e-aim-money',
      text: 'I manage my money actively.',
      protocol: [
        { id: 'e-aim-money-1', text: 'Review spending daily' },
        { id: 'e-aim-money-2', text: 'Stay within a set budget weekly' },
        { id: 'e-aim-money-3', text: 'Put something into savings every payday' },
      ],
    },
    {
      id: 'e-aim-protect',
      text: 'I protect what I have built.',
      protocol: [
        { id: 'e-aim-protect-1', text: 'Keep bills and obligations current, checked weekly' },
        { id: 'e-aim-protect-2', text: 'Check on one account, policy, or document that matters, monthly' },
        { id: 'e-aim-protect-3', text: 'Address any financial loose end within the week it appears' },
      ],
    },
    {
      id: 'e-aim-invest',
      text: 'I invest in my future instead of only my present.',
      protocol: [
        { id: 'e-aim-invest-1', text: 'Contribute to a long-term goal monthly' },
        { id: 'e-aim-invest-2', text: 'Learn one thing about managing money better, monthly' },
        { id: 'e-aim-invest-3', text: 'Review and rebalance allocations quarterly' },
      ],
    },
    {
      id: 'e-aim-career',
      text: 'I build my career and skills that earn.',
      protocol: [
        { id: 'e-aim-career-1', text: 'Work on a deliverable that advances my position weekly' },
        { id: 'e-aim-career-2', text: 'Invest time in a skill with professional return, weekly' },
        { id: 'e-aim-career-3', text: 'Explore one new opportunity or income stream monthly' },
      ],
    },
    {
      id: 'e-aim-value',
      text: 'I know and negotiate my worth.',
      protocol: [
        { id: 'e-aim-value-1', text: 'Track hours/output against what I am actually paid, monthly' },
        { id: 'e-aim-value-2', text: 'Ask for what something is worth at least once a quarter' },
        { id: 'e-aim-value-3', text: 'Reinvest one gain into a higher-leverage skill or asset' },
      ],
    },
    {
      id: 'e-aim-streams',
      text: 'I build systems that earn without needing me constantly present.',
      protocol: [
        { id: 'e-aim-streams-1', text: 'Spend one block weekly building an asset, not just trading time' },
        { id: 'e-aim-streams-2', text: 'Follow up on a professional opportunity before it goes cold' },
        { id: 'e-aim-streams-3', text: 'Review income streams quarterly for what is scalable vs. not' },
      ],
    },
  ],
};

export const SHAPE_CASCADE: Record<Sector, SectorCascade> = {
  S: SOCIAL,
  H: HEALTH,
  A: ADMIN,
  P: PERSONAL,
  E: ECONOMIC,
};
